import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VitalsTrend } from "../components/VitalsTrend";
import { openPrintWindow, esc, signatureAuditHtml, signatureMetaLine } from "../lib/print";
import { PageHeader, Card, Btn, Empty, Table, Td, Badge } from "../components/UI";
import { FLabel, FInput, FSelect, FTextarea, CheckBox } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { isLateEntry, LateEntryChip, LateEntryWarning } from "../lib/lateEntry";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { BodyMap } from "../components/BodyMap";
import { CssrsScreener, CSSRS_QUESTIONS } from "../components/CssrsScreener";
import { useClients, ClientSelect } from "./clinical/shared";
import { usePagination, PaginationBar } from "../components/Pagination";
import { FileText, Printer, PenLine, Trash2, Pencil, Plus, Search, FolderOpen, Library, UserCheck, MessageSquarePlus, Link2, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import { SignaturePromptModal } from "../components/SignaturePromptModal";
import { SignatureStamp } from "../components/SignaturePad";
import { useSaveShortcut, SAVE_HINT } from "../lib/useSaveShortcut";

const CAT_ORDER = ["Administrative", "Financial/Billing", "Orders", "Assessments", "Treatment Plans", "Transition/Discharge", "Progress Notes", "Clinical Forms", "Consent Forms"];

function TableField({ f, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const setCell = (i, k, v) => onChange(rows.map((r, ix) => (ix === i ? { ...r, [k]: v } : r)));
  return (
    <div className="rounded-xl border border-[#E5DFD3] overflow-hidden" data-testid={`cf-table-${f.key}`}>
      <table className="w-full text-xs">
        <thead><tr className="bg-[#F5F2EB] text-left">
          {f.columns.map((c) => <th key={c.key} className="px-2 py-1.5 font-semibold text-[#6D4C41]">{c.label}</th>)}
          <th className="w-8" /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#F1EEE7]">
              {f.columns.map((c) => (
                <td key={c.key} className="px-1 py-1">
                  <input data-testid={`cf-table-${f.key}-${i}-${c.key}`} value={r[c.key] || ""} onChange={(e) => setCell(i, c.key, e.target.value)}
                    className="w-full rounded-md border border-[#E5DFD3] px-2 py-1 text-xs focus:outline-none focus:border-[#8D6E63]" />
                </td>
              ))}
              <td className="px-1"><button type="button" onClick={() => onChange(rows.filter((_, ix) => ix !== i))} className="text-[#C62828] hover:bg-[#FFEBEE] rounded p-0.5"><Trash2 size={12} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" data-testid={`cf-table-${f.key}-add-row`} onClick={() => onChange([...rows, {}])}
        className="flex w-full items-center justify-center gap-1 border-t border-[#F1EEE7] py-1.5 text-[11px] font-semibold text-[#7B6157] hover:bg-[#FDFCF8]"><Plus size={11} /> Add row</button>
    </div>
  );
}

function FieldInput({ f, value, onChange }) {
  if (f.type === "textarea") return <FTextarea data-testid={`cf-field-${f.key}`} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "select") return (
    <FSelect data-testid={`cf-field-${f.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      {f.options.map((o) => <option key={o}>{o}</option>)}
    </FSelect>);
  if (f.type === "checkbox") return <CheckBox testId={`cf-field-${f.key}`} checked={!!value} onChange={() => onChange(!value)} label="Yes / Confirmed" />;
  if (f.type === "date") return <FInput data-testid={`cf-field-${f.key}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "time") return <FInput data-testid={`cf-field-${f.key}`} type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "number") return <FInput data-testid={`cf-field-${f.key}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "table") return <TableField f={f} value={value} onChange={onChange} />;
  if (f.type === "bodymap") return <BodyMap testId={`cf-field-${f.key}`} value={value} onChange={onChange} />;
  if (f.type === "cssrs") return <CssrsScreener testId={`cf-field-${f.key}`} value={value} onChange={onChange} />;
  return <FInput data-testid={`cf-field-${f.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

const VITAL_RANGES = {
  bp_systolic: [90, 140, "mmHg"], bp_diastolic: [60, 90, "mmHg"], heart_rate: [60, 100, "bpm"],
  resp_rate: [12, 20, "/min"], temperature: [97.0, 99.5, "°F"], o2_sat: [95, 100, "%"],
  pain: [0, 6, "/10", "Severe pain reported (7 or higher)"],
};

function vitalFlag(key, val) {
  const r = VITAL_RANGES[key];
  if (!r || val == null || val === "") return null;
  const n = Number(val);
  if (Number.isNaN(n)) return null;
  if (n < r[0]) return `Below normal range (${r[0]}-${r[1]} ${r[2]})`;
  if (n > r[1]) return r[3] || `Above normal range (${r[0]}-${r[1]} ${r[2]})`;
  return null;
}

function fieldPrint(f, v) {
  if (f.type === "checkbox") return v ? "☑ Yes" : "☐ No";
  if (f.type === "bodymap") {
    const marks = Array.isArray(v) ? v : [];
    if (!marks.length) return "-";
    return marks.map((m, i) => `${m.view === "back" ? "Back" : "Front"} #${i + 1}: ${esc(m.note || "unlabeled")} (${Math.round(m.x)}%, ${Math.round(m.y)}%)`).join("<br/>");
  }
  if (f.type === "cssrs") {
    const a = (v && v.answers) || [];
    const qs = CSSRS_QUESTIONS.map((q, i) => `${i + 1}. ${esc(q)} - <b>${esc(a[i] || "Not answered")}</b>`).join("<br/>");
    return `${qs}<br/><b>Risk level: ${esc((v && v.risk) || "Screening incomplete")}</b>`;
  }
  if (f.type === "table") {
    const rows = Array.isArray(v) ? v : [];
    if (!rows.length) return "-";
    const head = `<tr>${f.columns.map((c) => `<th style="text-align:left;border:1px solid #ccc;padding:3px 6px">${esc(c.label)}</th>`).join("")}</tr>`;
    const body = rows.map((r) => `<tr>${f.columns.map((c) => `<td style="border:1px solid #ccc;padding:3px 6px">${esc(r[c.key] || "")}</td>`).join("")}</tr>`).join("");
    return `<table style="border-collapse:collapse;width:100%;font-size:11px">${head}${body}</table>`;
  }
  return esc(v || "-");
}

function printForm(row, tpl) {
  if (!tpl) return;
  const body = tpl.sections.map((s) => `<h2>${esc(s.title)}</h2>` + s.fields.map((f) =>
    `<div class="label">${esc(f.label)}</div><div class="value" style="white-space:pre-wrap">${fieldPrint(f, row.data?.[f.key])}</div>`).join("")).join("");
  const goal = row.data?.linked_goal ? `<h2>Golden Thread - Linked Treatment Goal</h2><div class="value">${esc(row.data.linked_goal)}</div>` : "";
  const addenda = (row.addenda || []).map((a) => `<div class="value" style="border-left:3px solid #8D6E63;padding-left:8px;margin-top:6px"><b>ADDENDUM</b> - ${esc(a.author)}, ${dayjs(a.at).format("MMM D, YYYY h:mm A")}<br/>${esc(a.text)}</div>`).join("");
  const sigBlock = row.signed
    ? signatureAuditHtml({
        ...(row.signature || {}),
        signature_id: row.signature_id || (row.signature || {}).signature_id,
        signature_image: row.signature_image,
        signature_name: row.signature_name || row.signed_by,
        signed_by: row.signed_by,
        signed_at: row.signed_at,
        cosigned_by: row.cosigned_by,
        cosigned_at: row.cosigned_at,
      }, { label: "Clinician Signature" })
    : "";
  openPrintWindow(row.template_name, `
    <div class="clinic"><h1>${esc(row.template_name)}</h1>Client: ${esc(row.client_name)} · Date: ${esc(row.date)} · Author: ${esc(row.author)}</div>
    ${goal}${body}
    ${addenda ? `<h2>Addenda</h2>${addenda}` : ""}
    ${sigBlock}
    <div class="meta">${signatureMetaLine(row)} · CONFIDENTIAL, Protected Health Information (HIPAA)</div>`);
}

export default function ClinicalForms({ clientLock }) {
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("library");
  const [cat, setCat] = useState("All Documents");
  const [q, setQ] = useState("");
  const [filterClient, setFilterClient] = useState(clientLock || new URLSearchParams(window.location.search).get("client") || "");
  const [fill, setFill] = useState(null);
  const [clientId, setClientId] = useState(clientLock || "");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [data, setData] = useState({});
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const [addendumRow, setAddendumRow] = useState(null);
  const [addendumText, setAddendumText] = useState("");
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeSec, setActiveSec] = useState(0);
  const [vitalsNote, setVitalsNote] = useState(null);
  const [vitalsRows, setVitalsRows] = useState([]);
  const [signConfirm, setSignConfirm] = useState(false);
  const [attest, setAttest] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const { user: me } = useAuth();
  const clients = useClients();
  const pg = usePagination(rows);

  const load = () => api.get(`/clinical-forms${filterClient ? `?client_id=${filterClient}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { api.get("/clinical-forms/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [filterClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || templates.length === 0 || rows.length === 0) return;
    const fid = new URLSearchParams(window.location.search).get("form");
    if (fid) {
      const row = rows.find((r) => r.id === fid);
      const tpl = row && templates.find((t) => t.key === row.template_key);
      if (tpl) { setView("documents"); openFill(tpl, row); }
    }
    setDeepLinked(true);
  }, [rows, templates, deepLinked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!clientId || !fill || fill.category !== "Progress Notes") { setGoals([]); return; }
    api.get(`/treatment-plans?client_id=${clientId}`).then((r) => {
      const g = [];
      r.data.forEach((p) => (p.problems || []).forEach((pr) => (pr.goals || []).forEach((go) => go.goal && g.push(go.goal))));
      setGoals(g);
    }).catch(() => setGoals([]));
  }, [clientId, fill]);


  const openFill = (tpl, row) => {
    setFill(tpl); setEditRow(row || null); setActiveSec(0); setVitalsNote(null);
    setClientId(row?.client_id || clientLock || ""); setDate(row?.date || dayjs().format("YYYY-MM-DD")); setData(row?.data || {});
  };

  useEffect(() => {
    setVitalsNote(null); setVitalsRows([]);
    if (!fill || !clientId) return;
    if (!fill.sections.some((s) => s.fields.some((f) => f.key === "bp_systolic"))) return;
    api.get(`/clients/${clientId}/vitals`).then((r) => {
      setVitalsRows(r.data);
      const v = r.data[0];
      if (!v || editRow) return;
      const MAP = [["bp_systolic", "bp_systolic"], ["bp_diastolic", "bp_diastolic"], ["pulse", "heart_rate"],
        ["respiration", "resp_rate"], ["temperature", "temperature"], ["o2_saturation", "o2_sat"],
        ["weight_lbs", "weight"], ["height_in", "height"], ["pain_level", "pain"]];
      setData((d) => {
        const next = { ...d };
        let applied = 0;
        MAP.forEach(([src, dst]) => {
          if (v[src] != null && v[src] !== "" && (next[dst] == null || next[dst] === "")) { next[dst] = v[src]; applied += 1; }
        });
        return applied ? next : d;
      });
      setVitalsNote({ date: v.date, by: v.recorded_by });
    }).catch(() => {});
  }, [fill, clientId, editRow]);

  const jumpToSection = (i) => {
    setActiveSec(i);
    document.getElementById(`cf-sec-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!fill || fill.sections.length < 5) return;
    const scroller = document.querySelector('[data-testid="cf-fill-panel"] .overflow-y-auto');
    if (!scroller) return;
    const onScroll = () => {
      const top = scroller.getBoundingClientRect().top;
      let cur = 0;
      fill.sections.forEach((_, i) => {
        const el = document.getElementById(`cf-sec-${i}`);
        if (el && el.getBoundingClientRect().top - top <= 130) cur = i;
      });
      setActiveSec(cur);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [fill]);

  useSaveShortcut(() => { if (!busy) save(false); }, !!fill);
  const save = async (sign, sigPayload) => {
    if (!clientId) return toast.error("Select a client");
    setBusy(true);
    try {
      let saved;
      if (editRow) saved = (await api.put(`/clinical-forms/${editRow.id}`, { data, date })).data;
      else saved = (await api.post("/clinical-forms", { template_key: fill.key, client_id: clientId, date, data })).data;
      if (sign) {
        const body = sigPayload ? { name: sigPayload.name || "", image: sigPayload.image || "" } : {};
        await api.post(`/clinical-forms/${saved.id}/sign`, body);
      }
      toast.success(sign ? "Document signed, locked, and audit-stamped" : "Document saved as draft");
      setFill(null); setView("documents"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try { await api.delete(`/clinical-forms/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cosign = async (r) => {
    try { await api.post(`/clinical-forms/${r.id}/cosign`); toast.success("Document co-signed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveAddendum = async () => {
    try {
      await api.post(`/clinical-forms/${addendumRow.id}/addendum`, { text: addendumText });
      toast.success("Addendum appended - original note remains locked");
      setAddendumRow(null); setAddendumText(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const counts = useMemo(() => templates.reduce((a, t) => { a[t.category] = (a[t.category] || 0) + 1; return a; }, {}), [templates]);
  const visible = useMemo(() => {
    let list = templates;
    if (cat !== "All Documents") list = list.filter((t) => t.category === cat);
    if (q.trim()) list = list.filter((t) => t.name.toLowerCase().includes(q.trim().toLowerCase()));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, cat, q]);
  const tplOf = (row) => templates.find((t) => t.key === row.template_key);

  return (
    <div data-testid="clinical-forms-page">
      {!clientLock && (
        <PageHeader title="Clinical Forms" subtitle={`${templates.length} HIPAA-standard structured templates across ${CAT_ORDER.length} categories - Georgia-ready (1013/2013, GCAL)`} />
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[["library", "Forms Library", Library], ["documents", "Completed Documents", FolderOpen]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`cf-view-${k}`} onClick={() => setView(k)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            <Icon size={14} /> {l}{k === "documents" ? ` (${rows.length})` : ""}
          </button>
        ))}
        {view === "library" && (
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-3 top-2.5 text-[#84675B]" />
            <FInput data-testid="cf-search" placeholder="Search here" className="pl-8 h-9 w-64" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        )}
      </div>

      {view === "library" && (
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <Card className="w-full md:w-56 shrink-0 p-2 md:sticky md:top-[100px]" data-testid="cf-category-rail">
            {["All Documents", ...CAT_ORDER].map((c) => (
              <button key={c} data-testid={`cf-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setCat(c)}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-[13px] font-medium transition-colors ${cat === c ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
                <span className="truncate">{c}</span>
                <span className={`ml-2 rounded-full px-1.5 text-[10px] font-bold ${cat === c ? "bg-white/20" : "bg-[#F5F2EB] text-[#7B6157]"}`}>
                  {c === "All Documents" ? templates.length : counts[c] || 0}
                </span>
              </button>
            ))}
          </Card>
          <Card className="flex-1 min-w-0 p-5" data-testid="cf-template-catalog">
            <h3 className="mb-4 font-heading text-base font-bold text-[#3E2723]">{cat} <span className="ml-1 text-xs font-normal text-[#84675B]">{visible.length} form{visible.length === 1 ? "" : "s"}</span></h3>
            {visible.length === 0 ? <Empty text="No forms match your search." /> : (
              <div className="columns-1 sm:columns-2 xl:columns-3 gap-6 [column-fill:balance]">
                {visible.map((t) => (
                  <button key={t.key} data-testid={`cf-new-${t.key}`} onClick={() => openFill(t)}
                    className="group mb-1 flex w-full items-center gap-2 break-inside-avoid rounded-lg px-2 py-1.5 text-left text-sm text-[#3E2723] hover:bg-[#F5F2EB] transition-colors">
                    <FileText size={13} className="shrink-0 text-[#84675B] group-hover:text-[#6D4C41]" />
                    <span className="flex-1 leading-snug">{t.name}</span>
                    <Plus size={13} className="shrink-0 opacity-0 group-hover:opacity-100 text-[#6D4C41]" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {view === "documents" && (
        <>
          {!clientLock && (
            <div className="mb-3 w-64"><FLabel>Filter by client</FLabel><ClientSelect clients={clients} value={filterClient} onChange={setFilterClient} testId="cf-filter-client" /></div>
          )}
          <Card className="overflow-hidden" data-testid="cf-table">
            {rows.length === 0 ? <Empty text="No clinical documents yet - start from the Forms Library." /> : (
              <Table headers={["Date", "Document", "Category", "Client", "Author", "Status", ""]}>
                {pg.pageRows.map((r) => (
                  <tr key={r.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`cf-row-${r.id}`}>
                    <Td><span className="flex items-center gap-1.5">{dayjs(r.date).format("MM/DD/YYYY")}{isLateEntry(r.date, r.created_at) && <LateEntryChip id={r.id} />}</span></Td>
                    <Td className="font-medium">{r.template_name}</Td>
                    <Td>{r.category}</Td>
                    <Td>{r.client_name}</Td>
                    <Td className="text-[#7B6157]">{r.author}</Td>
                    <Td>{r.signed ? (
                      <span className="flex items-center gap-1">
                        <span className="rounded-full bg-[#E8F5E9] px-2.5 py-0.5 text-xs font-semibold text-[#2E7D32]">Signed</span>
                        {r.cosigned_by && <span title={`Co-signed by ${r.cosigned_by}`} className="text-[#2E7D32]"><UserCheck size={13} /></span>}
                        {(r.addenda || []).length > 0 && <span title={`${r.addenda.length} addendum(s)`} className="text-[#7B6157] text-[10px] font-bold">+{r.addenda.length}</span>}
                      </span>
                    ) : <Badge value={r.status} />}</Td>
                    <Td>
                      <div className="flex gap-1">
                        {!r.signed && <button data-testid={`cf-edit-${r.id}`} onClick={() => openFill(tplOf(r), r)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={14} /></button>}
                        {r.signed && <button data-testid={`cf-viewsigned-${r.id}`} title="View signed document with audit trail" onClick={() => openFill(tplOf(r), r)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Eye size={14} /></button>}
                        {r.signed && !r.cosigned_by && <button data-testid={`cf-cosign-${r.id}`} title="Co-sign (supervisor)" onClick={() => cosign(r)} className="rounded-lg p-1.5 text-[#2E7D32] hover:bg-[#E8F5E9]"><UserCheck size={14} /></button>}
                        {r.signed && <button data-testid={`cf-addendum-${r.id}`} title="Add addendum" onClick={() => { setAddendumRow(r); setAddendumText(""); }} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><MessageSquarePlus size={14} /></button>}
                        <button data-testid={`cf-print-${r.id}`} onClick={() => printForm(r, tplOf(r))} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={14} /></button>
                        <button data-testid={`cf-delete-${r.id}`} onClick={() => setDelRow(r)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={14} /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
            <PaginationBar {...pg} testId="cf-pagination" />
          </Card>
        </>
      )}

      <SlidePanel open={!!fill} onClose={() => setFill(null)} title={fill?.name || ""} subtitle={fill?.category} width={fill && fill.sections.length >= 5 ? "max-w-5xl" : "max-w-3xl"} testId="cf-fill-panel">
        {fill && (
          <div className={fill.sections.length >= 5 ? "flex items-start gap-5 pb-10" : "pb-10"}>
            {fill.sections.length >= 5 && (
              <nav className="sticky top-0 hidden max-h-[calc(100vh-170px)] w-48 shrink-0 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-2 md:block" data-testid="cf-section-nav">
                <div className="px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#84675B]">Sections</div>
                {fill.sections.map((s, i) => (
                  <button key={s.title} type="button" data-testid={`cf-section-nav-${i}`} onClick={() => jumpToSection(i)}
                    className={`block w-full truncate rounded-lg px-2.5 py-1.5 text-left text-xs font-medium transition-colors ${activeSec === i ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
                    {s.title}
                  </button>
                ))}
              </nav>
            )}
            <div className="min-w-0 flex-1 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={clientId} onChange={setClientId} testId="cf-client-select" disabled={!!editRow || !!clientLock} /></div>
              <div><FLabel>Document date</FLabel><DateField testId="cf-date" value={date} onChange={setDate} noFuture /><div className="mt-2"><LateEntryWarning date={date} /></div></div>
            </div>
            {vitalsNote && (
              <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] px-4 py-2.5 text-xs text-[#2E7D32]" data-testid="cf-vitals-prefill-note">
                <b>Vitals pre-filled</b> from the latest recorded vitals ({dayjs(vitalsNote.date).format("MMM D, YYYY")}{vitalsNote.by ? `, recorded by ${vitalsNote.by}` : ""}). Empty fields were filled; anything you typed was kept. Review and adjust as needed.
              </div>
            )}
            {fill.category === "Progress Notes" && goals.length > 0 && (
              <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] p-4" data-testid="cf-golden-thread">
                <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#2E7D32]"><Link2 size={14} /> Golden Thread - link this note to a treatment goal</div>
                <FSelect data-testid="cf-linked-goal" value={data.linked_goal || ""} onChange={(e) => setData((d) => ({ ...d, linked_goal: e.target.value }))}>
                  <option value="">No linked goal</option>
                  {goals.map((g, i) => <option key={i} value={g}>{g}</option>)}
                </FSelect>
              </div>
            )}
            {fill.sections.map((s, i) => (
              <div key={s.title} id={`cf-sec-${i}`} className={`rounded-xl border border-[#EFEBE0] p-4 ${editRow?.signed ? "pointer-events-none opacity-75" : ""}`}>
                <div className="mb-3 text-sm font-bold text-[#3E2723]">{s.title}</div>
                {vitalsRows.length > 0 && s.fields.some((f) => f.key === "bp_systolic") && <VitalsTrend rows={vitalsRows} />}
                <div className="space-y-3">
                  {s.fields.map((f) => {
                    const flag = vitalFlag(f.key, data[f.key]);
                    return (
                      <div key={f.key}><FLabel>{f.label}</FLabel>
                        <div className={flag ? "rounded-lg ring-2 ring-[#C62828]/70" : ""}>
                          <FieldInput f={f} value={data[f.key]} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} />
                        </div>
                        {flag && <div data-testid={`cf-abnormal-${f.key}`} className="mt-1 flex items-center gap-1 text-[11px] font-bold text-[#C62828]"><AlertTriangle size={11} /> {flag}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {editRow?.signed && (
              <div className="rounded-xl border border-[#E5DFD3] bg-[#FDFCF8] p-4" data-testid="cf-signature-audit">
                <SignatureStamp
                  sig={{
                    ...(editRow.signature || {}),
                    signature_id: editRow.signature_id || (editRow.signature || {}).signature_id,
                    signature_image: editRow.signature_image,
                    signature_name: editRow.signature_name,
                    signed_by: editRow.signed_by,
                    signed_at: editRow.signed_at,
                  }}
                  cosigned_by={editRow.cosigned_by}
                  cosigned_at={editRow.cosigned_at}
                  label="Clinician Signature"
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setFill(null)}>Cancel</Btn>
              <Btn variant="outline" onClick={() => save(false)} disabled={busy} data-testid="cf-save-draft-btn" title={`Save draft (${SAVE_HINT})`} aria-keyshortcuts="Control+S"><FileText size={14} /> Save Draft</Btn>
              <Btn onClick={() => { if (!clientId) return toast.error("Select a client"); setAttest(false); setSignConfirm(true); }} disabled={busy} data-testid="cf-sign-btn"><PenLine size={14} /> Sign & Lock</Btn>
            </div>
            </div>
          </div>
        )}
      </SlidePanel>
      {signConfirm && fill && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#3E2723]/40 p-4 backdrop-blur-sm" data-testid="cf-sign-confirm" onClick={() => setSignConfirm(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-[#EFEBE0] bg-[#FDFCF8] px-6 py-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F5F2EB] text-[#4E342E]"><PenLine size={16} /></span>
              <h3 className="font-heading text-base font-bold text-[#3E2723]">Sign & lock this document?</h3>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-xl bg-[#F5F2EB] p-4 text-sm text-[#3E2723]" data-testid="cf-sign-confirm-info">
                <div className="flex justify-between gap-3 py-0.5"><span className="text-[#7B6157]">Document</span><span className="min-w-0 truncate text-right font-semibold">{fill.name}</span></div>
                <div className="flex justify-between gap-3 py-0.5"><span className="text-[#7B6157]">Client</span><span className="font-semibold">{(() => { const c = clients.find((x) => x.id === clientId); return c ? `${c.first_name} ${c.last_name}` : "-"; })()}</span></div>
                <div className="flex justify-between gap-3 py-0.5"><span className="text-[#7B6157]">Date</span><span className="font-semibold">{dayjs(date).format("MMM D, YYYY")}</span></div>
                <div className="flex justify-between gap-3 py-0.5"><span className="text-[#7B6157]">Signing as</span><span className="font-semibold">{me?.first_name} {me?.last_name}</span></div>
              </div>
              <p className="text-xs text-[#7B6157]">Once signed, this document is locked and cannot be edited. Corrections require an addendum, and only an administrator can delete a signed document. The next step will let you apply your saved signature, or draw, type, or upload a new one.</p>
              {(() => {
                const flags = Object.keys(VITAL_RANGES).filter((k) => vitalFlag(k, data[k]));
                return flags.length > 0 && (
                  <div data-testid="cf-sign-abnormal-warning" className="flex items-start gap-2 rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] px-3.5 py-2.5 text-xs text-[#C62828]">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span><b>{flags.length} vital{flags.length === 1 ? " is" : "s are"} outside normal range.</b> Review the Physical Exam section before signing.</span>
                  </div>
                );
              })()}
              <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#E5DFD3] px-3.5 py-3 text-sm text-[#3E2723]">
                <input type="checkbox" data-testid="cf-sign-attest" checked={attest} onChange={(e) => setAttest(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4E342E]" />
                <span>I attest that this documentation is accurate, complete, and reflects the services provided.</span>
              </label>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setSignConfirm(false)}>Cancel</Btn>
                <Btn data-testid="cf-sign-confirm-btn" disabled={!attest || busy} onClick={() => { setSignConfirm(false); setSigOpen(true); }}>
                  <PenLine size={14} /> Continue to Signature
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}
      <SlidePanel open={!!addendumRow} onClose={() => setAddendumRow(null)} title="Add Addendum" subtitle={`${addendumRow?.template_name} - ${addendumRow?.client_name} (original stays locked)`} testId="cf-addendum-panel">
        <div className="space-y-4">
          {(addendumRow?.addenda || []).map((a, i) => (
            <div key={i} className="rounded-xl border-l-4 border-[#8D6E63] bg-[#FDFCF8] p-3 text-sm">
              <div className="text-xs font-semibold text-[#7B6157]">{a.author} · {dayjs(a.at).format("MMM D, YYYY h:mm A")}</div>
              <div className="mt-1 text-[#3E2723] whitespace-pre-wrap">{a.text}</div>
            </div>
          ))}
          <div><FLabel>Addendum text</FLabel>
            <FTextarea data-testid="cf-addendum-text" rows={5} value={addendumText} onChange={(e) => setAddendumText(e.target.value)}
              placeholder="Late entry / correction / additional clinical information…" /></div>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setAddendumRow(null)}>Cancel</Btn>
            <Btn onClick={saveAddendum} disabled={!addendumText.trim()} data-testid="cf-addendum-save-btn"><MessageSquarePlus size={14} /> Append Addendum</Btn>
          </div>
        </div>
      </SlidePanel>
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove}
        description={`Delete this ${delRow?.template_name} for ${delRow?.client_name}?${delRow?.signed ? " This document is SIGNED." : ""}`} />
      <SignaturePromptModal
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        onConfirm={async ({ image, name }) => { await save(true, { image, name }); setSigOpen(false); setAttest(false); }}
        title={fill ? `Sign ${fill.name}` : "Sign document"}
        subtitle={fill ? `${fill.name} for ${(clients.find((x) => x.id === clientId) || {}).first_name || ""} ${(clients.find((x) => x.id === clientId) || {}).last_name || ""}, dated ${dayjs(date).format("MMM D, YYYY")}. Locking is HIPAA-compliant with a full audit stamp.` : ""}
        busy={busy}
      />
    </div>
  );
}
